import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Toolbar,
  Typography,
  AppBar,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { lightPalette } from '../../../../theme';
import { classes } from './utils';
import { ANNOTATION } from '../../../../constants/staticContent';

const AnnotationSubNav = ({
  invoiceId,
  latestVersion,
  setLatestVersion,
  versionId,
  isEditing,
  invoiceVersions,
  version,
  setVersionChange,
  setSelectedVersionId,
  imageCount,
  carouselRef,
  currentPageIndex,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSelectChange = (event) => {
    const { value } = event.target;
    setVersionChange((prev) => !prev);
    setLatestVersion(value);
    let navigateTo;
    switch (true) {
      case location?.pathname.includes('invoice'):
        navigateTo = '/invoice';
        break;
      case location?.pathname.includes('sale'):
        navigateTo = '/sale';
        break;
      case location?.pathname.includes('receipt'):
        navigateTo = '/receipt';
        break;
      case location?.pathname.includes('creditcardstatement'):
        navigateTo = '/creditcardstatement';
        break;
      case location?.pathname.includes('bank-statement'):
        navigateTo = '/bank-statement';
        break;
      default:
        navigateTo = '/home'; // Optional: default route
    }
    if (value === invoiceVersions[0]?.version) {
      navigate(`${navigateTo}/${invoiceId}`);
    } else if (value) {
      navigate(`${navigateTo}/${value}/${invoiceId}`);
    }
  };

  if (versionId !== '') {
    setLatestVersion(version);
  } else if (invoiceVersions.length > 0) {
    setLatestVersion(invoiceVersions[0]?.version);
    setSelectedVersionId(invoiceVersions[0]?.id);
  }
  const previousImage = () => {
    if (carouselRef.current) {
      carouselRef.current.previous();
    }
  };
  const nextImage = () => {
    if (carouselRef.current) {
      carouselRef.current.next();
    }
  };
  let basePath;
  switch (true) {
    case location?.pathname.includes('invoice'):
      basePath = '/invoice';
      break;
    case location?.pathname.includes('sale'):
      basePath = '/sale';
      break;
    case location?.pathname.includes('receipt'):
      basePath = '/receipt';
      break;
    case location?.pathname.includes('creditcardstatement'):
      basePath = '/creditcardstatement';
      break;
    case location?.pathname.includes('bank-statement'):
      basePath = '/bank-statement';
      break;
    default:
      basePath = '/invoice';
  }
  return (
    <AppBar position="static" className={classes.subNavbar}>
      <Toolbar
        variant="dense"
        className={`${classes.subNavbarToolbar} ${
          versionId !== '' && isEditing === null
            ? classes.subNavbarToolbarJustifyContentConditionalSpaceBetween
            : classes.subNavbarToolbarJustifyContentFlexEnd
        }`}
      >
        {versionId !== '' && isEditing === null ? (
          <Typography variant="h5" color={lightPalette.secondaryBlack.main}>
            {ANNOTATION.READ_ONLY_MODE}{' '}
            <Link
              to={`${basePath}/${latestVersion}/${invoiceId}?editing=true`}
              className="linkStyle"
            >
              Edit.
            </Link>
          </Typography>
        ) : null}
        <Select
          value={latestVersion}
          onChange={handleSelectChange}
          className={classes.versionSelect}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '50vh',
              },
            },
          }}
        >
          {invoiceVersions?.map((option) => (
            <MenuItem key={option.id} value={option.version}>
              {option.version}
            </MenuItem>
          ))}
        </Select>
        {imageCount > 1 && (
          <Box className={classes.pagination}>
            {currentPageIndex !== 0 ? (
              <Box className={classes.arrowHoverButton}>
                <ArrowBackIosNewIcon
                  className={classes.arrowButton}
                  onClick={previousImage}
                />
              </Box>
            ) : (
              <Box className={classes.blankDiv} />
            )}
            <Typography variant="h7" color={lightPalette.secondaryBlack.main}>
              {`${currentPageIndex + 1} of ${imageCount}`}{' '}
            </Typography>
            {currentPageIndex + 1 !== imageCount ? (
              <Box className={classes.arrowHoverButton}>
                <ArrowForwardIosIcon
                  className={classes.arrowButton}
                  onClick={nextImage}
                />
              </Box>
            ) : (
              <Box className={classes.blankDiv} />
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};
AnnotationSubNav.defaultProps = {
  version: null,
  versionId: null,
  latestVersion: null,
  isEditing: null,
};

const VersionDataPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
});

AnnotationSubNav.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  versionId: PropTypes.string,
  latestVersion: PropTypes.string,
  setLatestVersion: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
  invoiceVersions: PropTypes.arrayOf(VersionDataPropTypes).isRequired,
  version: PropTypes.string,
  setVersionChange: PropTypes.func.isRequired,
  setSelectedVersionId: PropTypes.string.isRequired,
  imageCount: PropTypes.number.isRequired,
  carouselRef: PropTypes.shape({
    current: PropTypes.shape({
      previous: PropTypes.func,
      next: PropTypes.func,
    }),
  }).isRequired,
  currentPageIndex: PropTypes.number.isRequired,
};

export default AnnotationSubNav;
